import { Terminal } from "./terminal";

export enum HikOpenDoorType {
  FORCE_OPEN = "0", // keep open
  OPEN = "1", // open
  CLOSE = "2", // close
  FORCE_CLOSED = "3", // keep closed
}

/**
 * Identifiers for Hikvision resources.
 * Kept as strings but typed semantically for better clarity.
 */
export type HikDeviceCode = string;
export type HikDoorId = string;

/**
 * Options for operating door state.
 */
export interface HikDoorOpenOptions {
  deviceCode: HikDeviceCode;
  doorId: HikDoorId;
  typeOpen: HikOpenDoorType;
  /**
   * Duration in seconds for timed opening (if the device supports it).
   */
  durationSeconds?: number;
}

/**
 * Door-related API.
 * All functions generate an endpoint/route string.
 */
export interface HikDoorApis {
  /**
   * Builds the endpoint to change a door's state.
   */
  open(
    deviceCode: HikDeviceCode,
    doorId: HikDoorId,
    typeOpen: HikOpenDoorType
  ): string;

  /**
   * Gets a device by its code.
   */
  get(deviceCode: HikDeviceCode): string;

  /**
   * Variant with options object for greater extensibility.
   */
  openWithOptions(options: HikDoorOpenOptions): string;
}

/**
 * Device-related API.
 */
export interface HikDevicesApis {
  /**
   * Lists all devices.
   */
  all(): string;

  /**
   * Door operations on the device.
   */
  doors: HikDoorApis;
}

/**
 * Hikvision root API.
 */
export interface HikApis {
  url: string;
  devices: HikDevicesApis;
}

// 🔹 Generic API Response wrapper
export interface HikResponse<T = any> {
  code: string; // result code: "0" = success, others = failure
  msg: string; // descriptive message of the result
  data?: T; // payload object (depends on endpoint)
  metadata?: any; // some endpoints may include extra metadata
}

// ---------------------------------------------------------
// 🔹 Access Control Devices (acsDeviceList)
// ---------------------------------------------------------

/**
 * Access control device status enumeration
 */
export enum HikAcsDeviceStatus {
  UNKNOWN = 0, // unknown status
  ONLINE = 1, // device is online
  OFFLINE = 2, // device is offline
}

/**
 * Transfer protocol types for access control devices
 */
export enum HikTreatyType {
  HIKVISION_PRIVATE = "hiksdk_net", // Hikvision Private Protocol
  EHOME_REG = "ehome_reg", // ISUP-Intelligent Security Uplink Protocol
}

/**
 * Time zone information object
 */
export interface HikTimeZoneInfo {
  // Details to be defined based on TimeZoneInfo structure from API docs
  [key: string]: any;
}

// Request body for listing access control devices
export interface HikAcsDeviceListRequest {
  pageNo: number; // current page number (1 - 2147483647)
  pageSize: number; // number of records per page (1 - 500)
}

/**
 * Information about a single access control device
 */
export interface HikAcsDeviceInfo {
  /**
   * Access control device ID, up to 64 characters (Optional)
   */
  acsDevIndexCode?: string;

  /**
   * Access control device name, up to 32 characters (Optional)
   */
  acsDevName?: string;

  /**
   * IP address of access control device (Required)
   */
  acsDevIp: string;

  /**
   * Port No. of access control device (Required)
   */
  acsDevPort: string;

  /**
   * Serial No. of access control device, up to 16 characters (Optional)
   */
  acsDevCode?: string;

  /**
   * Transfer protocol type (Optional)
   */
  treatyType?: HikTreatyType | string;

  /**
   * Status of the access control device (Optional)
   */
  status?: HikAcsDeviceStatus;

  /**
   * Time zone information, returned for getting device info (Optional)
   */
  timeZone?: HikTimeZoneInfo;

  /**
   * Sipass configuration Terminal (Optional)
   */
  terminals?: Terminal[];
}

// Data object returned in the response
export interface HikListData<T = any> {
  total: number; // total number of devices
  pageNo: number; // current page number
  pageSize: number; // number of devices per page
  list: T[]; // list of device info objects
}

// Full response type for acsDeviceList API
export type HikAcsDeviceListResponse = HikResponse<
  HikListData<HikAcsDeviceInfo>
>;

// ---------------------------------------------------------
// 🔹 Access Control Doors/Access Points
// ---------------------------------------------------------

/**
 * Door/Access Point status enumeration
 */
export enum HikDoorState {
  REMAIN_OPEN = 0, // door remain open
  CLOSED = 1, // door closed
  OPEN = 2, // door open
  REMAIN_CLOSED = 3, // door remain closed
  OFFLINE = 4, // offline
}

/**
 * Exit direction door status
 */
export enum HikDoorOutState {
  INVALID = -1, // invalid field, no status for exit direction
  REMAIN_OPEN = 0, // remain open
  CLOSED = 1, // closed
  OPEN = 2, // open
  REMAIN_CLOSED = 3, // remain closed
  OFFLINE = 4, // offline
}

/**
 * Information about a door/access point
 */
export interface HikDoorInfo {
  /**
   * Access point ID, up to 64 characters
   */
  doorIndexCode?: string;

  /**
   * Access point name, up to 32 characters
   */
  doorName?: string;

  /**
   * Access point No. (or door No.), up to 16 characters
   */
  doorNo?: string;

  /**
   * Door status for entry direction
   * This field represents the door status for entry direction when doorOutState is not -1
   */
  doorState?: HikDoorState;

  /**
   * Door status for exit direction
   * -1 indicates there is no status for exit direction
   */
  doorOutState?: HikDoorOutState;

  /**
   * Access control device ID, up to 64 characters
   */
  acsDevIndexCode?: string;

  /**
   * Area ID, up to 64 characters
   */
  regionIndexCode?: string;
}

// Full response type for doorList API
export type HikDoorListResponse = HikResponse<HikListData<HikDoorInfo>>;
